import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Star, Quote, ChevronLeft, ChevronRight, X } from 'lucide-react';

const ReviewModal = ({ review, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            {review.fullName}
          </h3>
          <div className="flex gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i}
                className={`w-6 h-6 ${i < 5 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
              />
            ))}
          </div>
        </div>
        
        <p className="text-gray-700 leading-relaxed text-lg">
          {review.content}
        </p>
      </div>
    </div>
  );
};

const ReviewsSlider = ({ 
  subjectId = null,
  showTitle = true,
  title = "לקוחות ממליצים",
  maxReviews = 50,
  showControllers = true
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedReview, setSelectedReview] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        setLoading(true);
        let reviewsQuery;
        
        if (subjectId) {
          reviewsQuery = query(
            collection(db, 'reviews'),
            where('subjectId', '==', subjectId)
          );
        } else {
          reviewsQuery = collection(db, 'reviews');
        }

        const snapshot = await getDocs(reviewsQuery);
        const reviewsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Randomly sort and limit reviews
        const shuffledReviews = reviewsData
          .sort(() => Math.random() - 0.5)
          .slice(0, maxReviews);
        
        setReviews(shuffledReviews);
      } catch (error) {
        console.error('Error loading reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, [subjectId, maxReviews]);

  const scrollToReview = (index) => {
    if (containerRef.current) {
      const container = containerRef.current;
      const reviews = container.getElementsByClassName('review-card');
      if (reviews[index]) {
        reviews[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
        setCurrentIndex(index);
      }
    }
  };

  const scrollLeft = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToReview(newIndex);
  };

  const scrollRight = () => {
    const newIndex = Math.min(reviews.length - 1, currentIndex + 1);
    scrollToReview(newIndex);
  };

  if (loading) {
    return (
      <div className="py-20">
        <div className="flex justify-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <>
      {/* Review Modal */}
      {selectedReview && (
        <ReviewModal 
          review={selectedReview} 
          onClose={() => setSelectedReview(null)} 
        />
      )}

      <div className="py-24  bg-gray-100 [background-image:repeating-linear-gradient(60deg,rgba(53,67,78,0.1),rgba(53,67,78,0.1)_1px,transparent_1px,transparent_30px)] [background-size:60px_60px] relative overflow-hidden">
        {showTitle && (
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4 transform transition-transform hover:scale-105">
              {title}
            </h2>
            <div className="relative">
              <div className="w-32 h-1.5 bg-gradient-to-r from-[#97a9b6] to-[#9bc4e4]  mx-auto rounded-full"></div>
              <div className="w-16 h-1.5 bg-gradient-to-r  from-[#97a9b6] to-[#9bc4e4] mx-auto rounded-full -mt-0.5 blur-sm"></div>
            </div>
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4">
          {showControllers && reviews.length > 1 && (
            <>
              <button 
                onClick={scrollLeft}
                disabled={currentIndex === 0}
                className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 
                  bg-white/80 backdrop-blur-sm rounded-full p-4 
                  shadow-lg hover:bg-white hover:shadow-xl 
                  transition-all duration-300 group 
                  disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="גלול שמאלה"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
              </button>
              
              <button 
                onClick={scrollRight}
                disabled={currentIndex === reviews.length - 1}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 
                  bg-white/80 backdrop-blur-sm rounded-full p-4 
                  shadow-lg hover:bg-white hover:shadow-xl 
                  transition-all duration-300 group
                  disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="גלול ימינה"
              >
                <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
              </button>
            </>
          )}

          <div 
            ref={containerRef}
            className="flex gap-8 overflow-x-auto px-12 snap-x snap-mandatory hide-scrollbar pb-4 scroll-smooth 
              [&>*:first-child]:ml-0 [&>*:last-child]:mr-0
              sm:w-full sm:flex"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
            }}
          >
            {reviews.map((review, index) => (
              <div 
                key={review.id}
                className="review-card flex-shrink-0 w-80 sm:w-96 bg-white rounded-2xl 
                  shadow-md hover:shadow-xl p-8 snap-center 
                  transition-all duration-500 relative group
                  transform hover:-translate-y-2 hover:scale-105
                  mx-2 sm:mx-4"
              >
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 text-xl mb-2 truncate relative">
                    {review.fullName}
                  </h3>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i}
                        className={`w-5 h-5 transition-all duration-300 
                          ${i < 5 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="relative h-32 overflow-hidden">
                  <p className="text-gray-600 leading-relaxed text-lg line-clamp-3">
                    {review.content}
                  </p>
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
                </div>

                {review.content.length > 100 && (
                  <button 
                    className="mt-4 text-[#64748b] hover:text-blue-800 
                      transition-colors duration-300 text-sm font-semibold"
                    onClick={() => setSelectedReview(review)}
                  >
                    קרא עוד
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <style jsx global>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
            100% { transform: translateY(0px); }
          }
          
          .review-card {
            animation: float 6s ease-in-out infinite;
          }
        `}</style>
      </div>
    </>
  );
};

export default ReviewsSlider;